import * as React from 'react'
import { CLOUD_NAME, LIST_URL } from '../../data/cloudinary'

interface CloudinaryResource {
  public_id: string
  version: number
  format: string
  width: number
  height: number
  created_at: string
}

interface CloudinaryListResponse {
  resources: CloudinaryResource[]
}

const buildUrl = (r: CloudinaryResource): string =>
  `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/c_fill,w_480,h_360,q_auto,f_auto/v${
    r.version
  }/${r.public_id}.${r.format}`

const PAGE_SIZE = 18

const Gallery: React.FC = () => {
  const [allImages, setAllImages] = React.useState<string[]>([])
  const [visible, setVisible] = React.useState(PAGE_SIZE)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(false)

  React.useEffect(() => {
    fetch(LIST_URL)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch')
        return res.json() as Promise<CloudinaryListResponse>
      })
      .then(data => {
        const jobNum = (id: string) => {
          const m = id.match(/(\d+)/)
          return m ? parseInt(m[1], 10) : 0
        }
        const sorted = data.resources
          .slice()
          .sort((a, b) => jobNum(a.public_id) - jobNum(b.public_id))
        setAllImages(sorted.map(buildUrl))
        setLoading(false)
      })
      .catch(() => {
        setError(true)
        setLoading(false)
      })
  }, [])

  if (loading) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '4em',
          fontFamily: 'Oswald',
          color: '#666'
        }}
      >
        Loading gallery...
      </div>
    )
  }

  if (error || allImages.length === 0) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '3em',
          fontFamily: 'Oswald',
          color: '#888'
        }}
      >
        <p>Gallery unavailable — check Cloudinary tag setup.</p>
      </div>
    )
  }

  const images = allImages.slice(0, visible)
  const hasMore = visible < allImages.length

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '10px',
          padding: '0 1em'
        }}
      >
        {images.map((url, idx) => (
          <div
            key={idx}
            style={{
              overflow: 'hidden',
              borderRadius: '4px',
              aspectRatio: '4 / 3',
              backgroundColor: '#e0e0e0'
            }}
          >
            <img
              src={url}
              alt={`Elite Concrete project ${idx + 1}`}
              loading="lazy"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                transition: 'transform 0.3s ease'
              }}
              onMouseEnter={e =>
                ((e.currentTarget as HTMLImageElement).style.transform =
                  'scale(1.05)')
              }
              onMouseLeave={e =>
                ((e.currentTarget as HTMLImageElement).style.transform =
                  'scale(1)')
              }
            />
          </div>
        ))}
      </div>

      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: '2em' }}>
          <button
            onClick={() => setVisible(v => v + PAGE_SIZE)}
            style={{
              fontFamily: 'Oswald',
              fontSize: '1em',
              padding: '0.75em 2.5em',
              backgroundColor: '#1B263B',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              letterSpacing: '1px'
            }}
          >
            Load More
          </button>
        </div>
      )}
    </div>
  )
}

export default Gallery
